import React, { useEffect, useState, useRef } from 'react'
import {
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  DialogContentText,
  useTheme
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReCAPTCHA from 'react-google-recaptcha'

import CharacterCounter from './CharLimitMessage'
import useIsMobile from '../hooks/useIsMobile'
import ClosableModalTitle from './ClosableModalTitle'
import { fetchEnvironmentVar } from '../utils/miscUtils'

interface FormField {
  id: string
  label: string
  initialValue?: any
  type?: string
  required?: boolean
  multiline?: boolean
  rows?: number
  disabled?: boolean
}

interface GenericFormDialogProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  title: string
  fields: FormField[]
  apiFunction: (data: any) => Promise<any>
  preparePayload: (state: Record<string, string>) => any
  successMessage: string
  isSubmissionInvalid: (state: Record<string, string>) => boolean
}

const prepareInitialState = (fields: FormField[]): Record<string, any> =>
  fields.reduce((acc, field) => ({ ...acc, [field.id]: field.initialValue || '' }), {})


const GenericFormDialog: React.FC<GenericFormDialogProps> = ({
  open,
  setOpen,
  title,
  fields,
  apiFunction,
  preparePayload,
  successMessage,
  isSubmissionInvalid
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const inputCharLimit = 500
  const errorMessage = 'There was an error sending your request. Please try again later.'
  const [dialogState, setDialogState] = useState<Record<string, string>>(() =>
    prepareInitialState(fields)
  )
  const [isRecaptchaPassed, setIsRecaptchaPassed] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  useEffect(() => {
    // Reinitialize dialogState whenever the fields array changes.
    // doing this for when measure changes for new unit form.
    setDialogState(prepareInitialState(fields))
  }, [fields])

    useEffect(() => {
    // This runs when the dialog closes
    if (!open && isRecaptchaPassed) {
      setIsRecaptchaPassed(false)

      // Reset the actual reCAPTCHA widget
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
    }
  }, [open, dialogState.isRecaptchaPassed])


  const handleDialogFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.value.length > inputCharLimit) {
      return
    }

    const { id, value } = event.target
    setDialogState((prevState) => ({
      ...prevState,
      [id]: value
    }))
  }

  const sendMessage = useMutation({
    mutationFn: apiFunction,
    onSuccess: () => {
      setDialogState(prepareInitialState(fields))
      setIsRecaptchaPassed(false)
      toast.info(successMessage)
    },
    onError: (error) => {
      console.error('Error submitting request:', error)
      toast.error('There was an error sending your request. Please try again.')
      // todo: sentry
    }
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    sendMessage.mutate(preparePayload(dialogState))
  }

  const handleClose = (): void => {
    setOpen(false)
    setTimeout(() => sendMessage.reset(), 1000) // I know...
  }

  const handleOnChangeReCAPTCHA = (): void => {
    const reValue: string = recaptchaRef.current.getValue()
    setIsRecaptchaPassed(!!reValue)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '100%', sm: '500px' }
        }
      }}
    >
      <Box sx={{ padding: { xs: 1, sm: 4 }, width: '100%' }}>
        {!sendMessage.isPending && !sendMessage.isSuccess && (
          <form onSubmit={handleSubmit}>
            <ClosableModalTitle title={title} onClose={handleClose} marginLeft='-7px' />
            {sendMessage.isError && (
              <DialogContentText sx={{ color: theme.palette.custom.attentionRed }}>
                {errorMessage}
              </DialogContentText>
            )}
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                {fields.map((field) => (
                  <TextField
                    key={field.id}
                    margin="dense"
                    id={field.id}
                    value={dialogState[field.id]}
                    onChange={handleDialogFieldChange}
                    label={field.label}
                    type={field.type || 'text'}
                    required={field.required || false}
                    variant="outlined"
                    multiline={field.multiline || false}
                    rows={field.rows || undefined}
                    disabled={field.disabled || false}
                  />
                ))}
                <CharacterCounter text={dialogState.message} charLimit={inputCharLimit} />
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="normal"
                  sitekey={fetchEnvironmentVar('REACT_APP_GOOGLE_SITE_KEY')}
                  onChange={handleOnChangeReCAPTCHA}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: 3 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: theme.palette.custom.beautifulGradient
                }}
                type="submit"
                disabled={isSubmissionInvalid(dialogState) || !isRecaptchaPassed}
              >
                Submit <FontAwesomeIcon icon={faEnvelope} style={{ marginLeft: '8px', fontSize: '1rem' }} />
              </Button>
            </DialogActions>
          </form>
        )}
        {sendMessage.isPending && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {sendMessage.isSuccess && (
          <DialogContentText>
            {successMessage}
          </DialogContentText>
        )}
        {sendMessage.isError && <DialogContentText>
          {errorMessage}
        </DialogContentText>}
      </Box>
    </Dialog>
  )
}

export default GenericFormDialog
